import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { QQQuestionnaireViewModel } from 'src/app/models/qq-questionnaire.view-model';
import { QuickQuoteRequest } from 'src/app/models/quick-quote-request';
import { QuickQuoteResult } from 'src/app/models/quick-quote-result';
import { AnswerModel, QuoteModel } from 'src/app/models/quote.model';
import { SiteContentModel } from 'src/app/models/site-content.model';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ApiService } from 'src/app/services/api.service';
import { SiteContentService } from 'src/app/services/site-content.service';

@Component({
  selector: 'app-apg-quick-quote-complete',
  templateUrl: './apg-quick-quote-complete.component.html',
  styleUrls: ['./apg-quick-quote-complete.component.scss'],
})
export class ApgQuickQuoteCompleteComponent implements OnInit {

  @Input() quickQuote: QQQuestionnaireViewModel;
  @Output() continue = new EventEmitter<QuoteModel>();

  loading: boolean;
  quoteLTA: QuoteModel;
  quoteDTA: QuoteModel;
  error: string;
  thankyou: SiteContentModel;
  answersObject: any;
  sumAssuredLabel: string;

  constructor(
    private api: ApiService,
    private siteContentService: SiteContentService,
    private analyticsService: AnalyticsService
  ) { }

  async ngOnInit() {
    if (this.quickQuote.answersObject['CoverSource'] === 'CoverCalculator')
      await this.getCoverBasisQuote();
    else
      await this.getResults();
  }

  async getResults() {
    this.loading = true;
    this.quoteLTA = null;
    this.quoteDTA = null;
    this.answersObject = this.quickQuote.answersObject;

    this.setSumAssuredLabel();

    try {
      const [rspLTA, rspDTA] = await this.getComparativeQuotes();

      if (rspLTA.error) {
        this.error = rspLTA.error;
        return;
      }

      if (rspDTA.error) {
        this.error = rspDTA.error;
        return;
      }

      await this.loadThankYou();

      this.quoteLTA = rspLTA.quote;
      this.quoteDTA = rspDTA.quote;

      this.analyticsService.logLead(this.quickQuote, this.quoteDTA.result,'quick_quote');
    } catch (err) {
      let content = await this.siteContentService.getSiteContent("QuickQuote.ApiAccessError");
      this.error = content.body;
      this.analyticsService.logFailedLead(this.quickQuote);
    } finally {
      this.loading = false;
    }

  }

  buildComparativeAnswers(coverBasis: 'LEVEL' | 'DECREASING', policyType: 'TERM' | 'TERM-CI') {
    const answers = [...this.quickQuote.answers.entries()]
      .filter(([key]) => {
        return this.quickQuote.answersObject["TypeOfPolicy_1"] !== 'SINGLE' || !key.startsWith('SecondPolicyHolder');
      })
      .map(([key, value]) => new AnswerModel(key, value?.toString()));

    //const answers = [...this.quickQuote.answers.entries()]
      //.map(([key, value]) => (new AnswerModel(key, value?.toString())));

    setAnswer(answers, 'CoverBasis_1', coverBasis);
    setAnswer(answers, 'PolicyType_1', policyType);

    return answers;
  }

  async getComparativeQuotes() {
    const answers = [
      this.buildComparativeAnswers('LEVEL', 'TERM'),
      this.buildComparativeAnswers('DECREASING', 'TERM')
    ];

    const results = await this.api.comparativeQuotes(this.quickQuote.name, this.quickQuote.quoteKey, true, answers);

    return results.map((x, i) => this.mapQuoteResult(x, answers[i]));
  }

  mapQuoteResult(rsp: QuickQuoteResult, answers: AnswerModel[]) {
    if (rsp.error) {
      this.error = rsp.error;
      return { error: rsp.error };
    }

    this.quickQuote.saveAnswer("Uid", rsp.uid);

    if (answers.find(x => x.key == 'FinancialAllocationType')?.value == "Premium") {
      this.quickQuote.saveAnswer("ProtectionLevel_1", rsp.sumAssured.toString());
      setAnswer(answers, 'ProtectionLevel_1', rsp.sumAssured.toString())
    }

    const quote = new QuoteModel(this.quickQuote.name, {
      key: rsp.key,
      answers,
      result: rsp,
      parentKey: this.quickQuote.quoteKey
    });

    return { quote };

  }

  setSumAssuredLabel() {
    const ans = (key: string) => this.quickQuote.answersObject[key] || '';
    const inclCi = ans('PolicyType_2');
    this.sumAssuredLabel = "Life Cover of";
    if (inclCi == 'TERM-CI') {
      this.sumAssuredLabel = "Life with Multi-Claim Critical Illness Cover of";
    }
  }

  private async loadThankYou() {
    const channel = this.quickQuote.answers.get('PreferredChannel');
    return await this.siteContentService.getSiteContent(`QuickQuote.ThankYou${channel}`);
  }

  prevPage() {
    this.quickQuote.saveAnswer('CoverSource', null)
    this.quickQuote.back(3);
  }  
  
  async getCoverBasisQuote() //to cater for quotes created using cover calculator i.e CoverBasis already set
  {
    this.loading = true;
    try {
      
      const kvp = [...this.quickQuote.answers.entries()]
      .map(x => ({ Key: x[0], Value: x[1] }));

      const request = new QuickQuoteRequest(null, kvp, true); 
      const rsp = await this.api.submitQuickQuote(this.quickQuote.questionnaire.name, request);

      const answers = [...this.quickQuote.answers.entries()]
      .map(([key, value]) => (new AnswerModel(key, value?.toString())));

      var result = this.mapQuoteResult(rsp, answers);
      if (result.error) {
        this.error = result.error;
        return;
      }
      this.continue.emit(result.quote);

    } catch (err) {
      let content = await this.siteContentService.getSiteContent("QuickQuote.ApiAccessError");
      this.error = content.body;
    } finally {
      this.loading = false;
    }    
  }
}

function setAnswer(answers: AnswerModel[], key: string, value: string) {
  const answer = answers.find(x => x.key == key);
  if (answer)
    answer.value = value;
  else
    answers.push(new AnswerModel(key, value));
}

